import type { Upload as S3Upload } from '@aws-sdk/lib-storage'

import Command from '~/client/src/shared/commands/Command'
import MapboxService, {
  IS3Config,
} from '~/client/src/shared/services/MapboxService'

const REGION = 'us-east-1'
const PART_SIZE = 10 * 1024 * 1024 // 10MB chunks
const QUEUE_SIZE = 4 // 4 parallel uploads

export type UploadProgressCallback = (loaded: number, total: number) => void

export default class UploadFileToMapboxS3Command implements Command {
  private managedUpload: S3Upload | null = null

  public constructor(
    private readonly file: Blob,
    private readonly mapboxService: MapboxService,
  ) {}

  public async execute(
    onProgress?: UploadProgressCallback,
  ): Promise<IS3Config> {
    const config = await this.getUploadingConfig()
    const S3_BUCKET = config.bucket

    const [{ S3Client }, { Upload }] = await Promise.all([
      import('@aws-sdk/client-s3'),
      import('@aws-sdk/lib-storage'),
    ])

    const s3 = new S3Client({
      credentials: {
        accessKeyId: config.accessKeyId,
        secretAccessKey: config.secretAccessKey,
        sessionToken: config.sessionToken,
      },
      // Avoid optional multipart checksum negotiation in the browser.
      requestChecksumCalculation: 'WHEN_REQUIRED',
      region: REGION,
    })

    const params = {
      Bucket: S3_BUCKET,
      Key: config.key,
      Body: this.file,
    }

    this.managedUpload = new Upload({
      client: s3,
      params,
      partSize: PART_SIZE,
      queueSize: QUEUE_SIZE,
    })

    if (onProgress) {
      this.managedUpload.on('httpUploadProgress', progress => {
        onProgress(progress.loaded || 0, progress.total || this.file.size)
      })
    }

    await this.managedUpload.done()
    this.managedUpload = null

    return config
  }

  public abort(): void {
    if (this.managedUpload) {
      void this.managedUpload.abort()
      this.managedUpload = null
    }
  }

  private async getUploadingConfig(): Promise<IS3Config> {
    return this.mapboxService.getS3Config()
  }
}
